// src/components/home/FeaturesSection.tsx
'use client';

import { Container, Box, Typography, Card, CardContent, Avatar } from '@mui/material';
import { motion } from 'framer-motion';
import { keyframes } from '@emotion/react';
import { features } from '@/data/features'; 

// Animación de flotación para los íconos
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

// Animación del fondo tipo “ondas suaves”
const wave = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

export default function FeaturesSection() {
  return (
    <Box
      id="features"
      sx={{
        py: 8,
        bgcolor: 'background.default',
        backgroundSize: '200% 200%',
        animation: `${wave} 18s ease-in-out infinite`,
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          fontWeight={600}
          color="primary"
        >
          Tecnología Marina
        </Typography>

        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}
        >
          Como líderes en investigación marina y desarrollo tecnológico, en Vixia System diseñamos e implementamos:
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1fr 1fr 1fr',
            },
            gap: 4,
          }}
        >
          {features.map((feature, index) => {
            const IconComponent = feature.icono;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 3,
                    borderRadius: 4,
                    backgroundColor: 'white',
                    transition: 'transform 0.25s, box-shadow 0.25s, background-color 0.25s',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      backgroundColor: `${feature.color}10`,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${feature.color}15`,
                          color: feature.color,
                          width: 70,
                          height: 70,
                          animation: `${float} 3.5s ease-in-out infinite`,
                        }}
                      >
                        <IconComponent sx={{ fontSize: 35 }} />
                      </Avatar>
                    </Box>

                    <Typography
                      variant="h6"
                      component="h3"
                      gutterBottom
                      fontWeight={600}
                      sx={{
                        minHeight: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: feature.color,
                      }}
                    >
                      {feature.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}
